import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminBrandingPage() {
  const [settings, setSettings] = useState(null); // null вместо захардкоженных значений
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/admin/branding', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Не удалось загрузить настройки');
        const data = await response.json();
        setSettings({
          // Основные настройки
          logo_url: data.logo_url || '',
          favicon_url: data.favicon_url || '',
          site_name: data.site_name || '',
          site_subtitle: data.site_subtitle || '',
          
          // Тексты для авторизации
          login_welcome_text: data.login_welcome_text || '',
          register_welcome_text: data.register_welcome_text || '',
          footer_text: data.footer_text || '',
          
          // Дашборд - секции
          dashboard_servers_title: data.dashboard_servers_title || '',
          dashboard_servers_description: data.dashboard_servers_description || '',
          dashboard_tariffs_title: data.dashboard_tariffs_title || '',
          dashboard_tariffs_description: data.dashboard_tariffs_description || '',
          dashboard_tagline: data.dashboard_tagline || '',
          dashboard_referrals_title: data.dashboard_referrals_title || '',
          dashboard_referrals_description: data.dashboard_referrals_description || '',
          dashboard_support_title: data.dashboard_support_title || '',
          dashboard_support_description: data.dashboard_support_description || '',
          
          // Названия для тарифов
          tariff_tier_basic_name: data.tariff_tier_basic_name || '',
          tariff_tier_pro_name: data.tariff_tier_pro_name || '',
          tariff_tier_elite_name: data.tariff_tier_elite_name || '',
          
          // Названия функций тарифов (JSON объект)
          tariff_features_names: data.tariff_features_names || {},
          
          // Тексты кнопок
          button_subscribe_text: data.button_subscribe_text || '',
          button_buy_text: data.button_buy_text || '',
          button_connect_text: data.button_connect_text || '',
          button_share_text: data.button_share_text || '',
          button_copy_text: data.button_copy_text || '',
          
          // Мета-теги
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          meta_keywords: data.meta_keywords || '',
          
          // Быстрое скачивание
          quick_download_enabled: data.quick_download_enabled !== false,
          quick_download_windows_url: data.quick_download_windows_url || '',
          quick_download_android_url: data.quick_download_android_url || '',
          quick_download_macos_url: data.quick_download_macos_url || '',
          quick_download_ios_url: data.quick_download_ios_url || '',
          quick_download_profile_deeplink: data.quick_download_profile_deeplink || '',
          
          // Дополнительные тексты
          subscription_active_text: data.subscription_active_text || '',
          subscription_expired_text: data.subscription_expired_text || '',
          subscription_trial_text: data.subscription_trial_text || '',
          balance_label_text: data.balance_label_text || '',
          referral_code_label_text: data.referral_code_label_text || ''
        });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    
    try {
      const response = await fetch('/api/admin/branding', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка сохранения');
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      setError(e.message);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка...</div></section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">Брендинг</h2>
        <p className="admin-description-text">
          Настройте внешний вид и тексты сайта. Измените логотип, название и приветственные сообщения.
        </p>
        
        {error && <div className="message-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="settings-form-group">
            <label htmlFor="logo_url" className="settings-label">
              URL логотипа
            </label>
            <input
              type="url"
              id="logo_url"
              value={settings.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              className="settings-input"
              placeholder="https://example.com/logo.png"
            />
            <p className="settings-hint">
              Введите полный URL изображения логотипа. Рекомендуемый размер: 200x50px или больше.
            </p>
            {settings.logo_url && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={settings.logo_url} 
                  alt="Logo preview" 
                  style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{ display: 'none', color: 'var(--text-secondary)', fontSize: '12px' }}>
                  Не удалось загрузить изображение. Проверьте URL.
                </div>
              </div>
            )}
          </div>

          <div className="settings-form-group">
            <label htmlFor="site_name" className="settings-label">
              Название сайта
            </label>
            <input
              type="text"
              id="site_name"
              value={settings.site_name}
              onChange={(e) => handleChange('site_name', e.target.value)}
              className="settings-input"
              placeholder="StealthNET"
              maxLength={100}
            />
            <p className="settings-hint">
              Название будет отображаться в заголовке страницы авторизации.
            </p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="site_subtitle" className="settings-label">
              Подзаголовок сайта
            </label>
            <input
              type="text"
              id="site_subtitle"
              value={settings.site_subtitle}
              onChange={(e) => handleChange('site_subtitle', e.target.value)}
              className="settings-input"
              placeholder="Добро пожаловать в будущее безопасности."
              maxLength={200}
            />
            <p className="settings-hint">
              Подзаголовок отображается под названием на странице авторизации.
            </p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="login_welcome_text" className="settings-label">
              Текст приветствия (страница входа)
            </label>
            <textarea
              id="login_welcome_text"
              value={settings.login_welcome_text}
              onChange={(e) => handleChange('login_welcome_text', e.target.value)}
              className="settings-textarea"
              placeholder="Добро пожаловать в будущее безопасности."
              maxLength={200}
              rows={3}
            />
            <p className="settings-hint">
              Текст, который будет отображаться на странице входа. Оставьте пустым для использования подзаголовка.
            </p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="register_welcome_text" className="settings-label">
              Текст приветствия (страница регистрации)
            </label>
            <textarea
              id="register_welcome_text"
              value={settings.register_welcome_text}
              onChange={(e) => handleChange('register_welcome_text', e.target.value)}
              className="settings-textarea"
              placeholder="Присоединяйтесь к невидимой сети."
              maxLength={200}
              rows={3}
            />
            <p className="settings-hint">
              Текст, который будет отображаться на странице регистрации. Оставьте пустым для использования подзаголовка.
            </p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="footer_text" className="settings-label">
              Текст в футере
            </label>
            <input
              type="text"
              id="footer_text"
              value={settings.footer_text}
              onChange={(e) => handleChange('footer_text', e.target.value)}
              className="settings-input"
              placeholder="© 2025 StealthNET. Privacy First."
              maxLength={200}
            />
            <p className="settings-hint">
              Текст, который будет отображаться в футере страницы авторизации.
            </p>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Настройки Dashboard (Клиентская панель)</h3>
            
            <div className="settings-form-group">
              <label htmlFor="dashboard_tagline" className="settings-label">
                Слоган в сайдбаре
              </label>
              <input
                type="text"
                id="dashboard_tagline"
                value={settings.dashboard_tagline}
                onChange={(e) => handleChange('dashboard_tagline', e.target.value)}
                className="settings-input"
                placeholder="Secure VPN"
                maxLength={100}
              />
              <p className="settings-hint">
              Текст, который будет отображаться под названием в боковом меню Dashboard.
            </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_servers_title" className="settings-label">
                Заголовок страницы серверов
              </label>
              <input
                type="text"
                id="dashboard_servers_title"
                value={settings.dashboard_servers_title}
                onChange={(e) => handleChange('dashboard_servers_title', e.target.value)}
                className="settings-input"
                placeholder="Глобальная сеть StealthNET"
                maxLength={200}
              />
              <p className="settings-hint">
                Заголовок на странице серверов в Dashboard. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_servers_description" className="settings-label">
                Описание страницы серверов
              </label>
              <textarea
                id="dashboard_servers_description"
                value={settings.dashboard_servers_description}
                onChange={(e) => handleChange('dashboard_servers_description', e.target.value)}
                className="settings-textarea"
                placeholder="Держим низкий пинг и высокую скорость, чтобы VPN чувствовался как прямое подключение."
                maxLength={300}
                rows={3}
              />
              <p className="settings-hint">
                Описание на странице серверов. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_tariffs_title" className="settings-label">
                Заголовок страницы тарифов
              </label>
              <input
                type="text"
                id="dashboard_tariffs_title"
                value={settings.dashboard_tariffs_title}
                onChange={(e) => handleChange('dashboard_tariffs_title', e.target.value)}
                className="settings-input"
                placeholder="Премиум VPN без лимитов"
                maxLength={200}
              />
              <p className="settings-hint">
                Заголовок на странице тарифов в Dashboard. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_tariffs_description" className="settings-label">
                Описание страницы тарифов
              </label>
              <textarea
                id="dashboard_tariffs_description"
                value={settings.dashboard_tariffs_description}
                onChange={(e) => handleChange('dashboard_tariffs_description', e.target.value)}
                className="settings-textarea"
                placeholder="Выберите срок подписки и мгновенно получите доступ к всем серверам и новому ключу подключения."
                maxLength={300}
                rows={3}
              />
              <p className="settings-hint">
                Описание на странице тарифов. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_referrals_title" className="settings-label">
                Заголовок страницы рефералов
              </label>
              <input
                type="text"
                id="dashboard_referrals_title"
                value={settings.dashboard_referrals_title}
                onChange={(e) => handleChange('dashboard_referrals_title', e.target.value)}
                className="settings-input"
                placeholder="Реферальная программа"
                maxLength={200}
              />
              <p className="settings-hint">
                Заголовок на странице рефералов в Dashboard.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_referrals_description" className="settings-label">
                Описание страницы рефералов
              </label>
              <textarea
                id="dashboard_referrals_description"
                value={settings.dashboard_referrals_description}
                onChange={(e) => handleChange('dashboard_referrals_description', e.target.value)}
                className="settings-textarea"
                placeholder="Приглашайте друзей и получайте бонусы"
                maxLength={300}
                rows={3}
              />
              <p className="settings-hint">
                Описание на странице рефералов.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_support_title" className="settings-label">
                Заголовок страницы поддержки
              </label>
              <input
                type="text"
                id="dashboard_support_title"
                value={settings.dashboard_support_title}
                onChange={(e) => handleChange('dashboard_support_title', e.target.value)}
                className="settings-input"
                placeholder="Техническая поддержка"
                maxLength={200}
              />
              <p className="settings-hint">
                Заголовок на странице поддержки в Dashboard.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="dashboard_support_description" className="settings-label">
                Описание страницы поддержки
              </label>
              <textarea
                id="dashboard_support_description"
                value={settings.dashboard_support_description}
                onChange={(e) => handleChange('dashboard_support_description', e.target.value)}
                className="settings-textarea"
                placeholder="Мы всегда готовы помочь"
                maxLength={300}
                rows={3}
              />
              <p className="settings-hint">
                Описание на странице поддержки.
              </p>
            </div>
          </div>

          {/* Названия тарифов и функций */}
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>🏷️ Названия тарифов</h3>
            <p className="admin-description-text" style={{ marginBottom: '1.5rem' }}>
              Настройте кастомные названия для уровней тарифов (basic, pro, elite).
            </p>
            
            <div className="settings-form-group">
              <label htmlFor="tariff_tier_basic_name" className="settings-label">
                Название для Basic тарифа
              </label>
              <input
                type="text"
                id="tariff_tier_basic_name"
                value={settings.tariff_tier_basic_name}
                onChange={(e) => handleChange('tariff_tier_basic_name', e.target.value)}
                className="settings-input"
                placeholder="Базовый"
                maxLength={100}
              />
              <p className="settings-hint">
                Кастомное название для тарифов уровня Basic. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="tariff_tier_pro_name" className="settings-label">
                Название для Pro тарифа
              </label>
              <input
                type="text"
                id="tariff_tier_pro_name"
                value={settings.tariff_tier_pro_name}
                onChange={(e) => handleChange('tariff_tier_pro_name', e.target.value)}
                className="settings-input"
                placeholder="Премиум"
                maxLength={100}
              />
              <p className="settings-hint">
                Кастомное название для тарифов уровня Pro. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="tariff_tier_elite_name" className="settings-label">
                Название для Elite тарифа
              </label>
              <input
                type="text"
                id="tariff_tier_elite_name"
                value={settings.tariff_tier_elite_name}
                onChange={(e) => handleChange('tariff_tier_elite_name', e.target.value)}
                className="settings-input"
                placeholder="Элитный"
                maxLength={100}
              />
              <p className="settings-hint">
                Кастомное название для тарифов уровня Elite. Оставьте пустым для использования значения по умолчанию.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="tariff_features_names" className="settings-label">
                Кастомизация названий функций тарифов (JSON)
              </label>
              <textarea
                id="tariff_features_names"
                value={JSON.stringify(settings.tariff_features_names || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    handleChange('tariff_features_names', parsed);
                  } catch {
                    // Игнорируем ошибки парсинга при вводе
                  }
                }}
                className="settings-textarea"
                placeholder='{"Безлимитный трафик": "Неограниченный интернет", "До 5 устройств": "Подключение 5 устройств"}'
                rows={6}
                style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
              />
              <p className="settings-hint">
                JSON объект для замены названий функций тарифов. Ключ - оригинальное название, значение - новое название.<br/>
                <strong>Пример:</strong> <code>{"{"}"Безлимитный трафик": "Неограниченный интернет"{"}"}</code>
              </p>
            </div>
          </div>

          {/* Тексты кнопок */}
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>🔘 Тексты кнопок</h3>
            <p className="admin-description-text" style={{ marginBottom: '1.5rem' }}>
              Настройте тексты для кнопок интерфейса.
            </p>
            
            <div className="settings-form-group">
              <label htmlFor="button_subscribe_text" className="settings-label">
                Текст кнопки "Подписаться"
              </label>
              <input
                type="text"
                id="button_subscribe_text"
                value={settings.button_subscribe_text}
                onChange={(e) => handleChange('button_subscribe_text', e.target.value)}
                className="settings-input"
                placeholder="Подписаться"
                maxLength={50}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="button_buy_text" className="settings-label">
                Текст кнопки "Купить"
              </label>
              <input
                type="text"
                id="button_buy_text"
                value={settings.button_buy_text}
                onChange={(e) => handleChange('button_buy_text', e.target.value)}
                className="settings-input"
                placeholder="Купить"
                maxLength={50}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="button_connect_text" className="settings-label">
                Текст кнопки "Подключить"
              </label>
              <input
                type="text"
                id="button_connect_text"
                value={settings.button_connect_text}
                onChange={(e) => handleChange('button_connect_text', e.target.value)}
                className="settings-input"
                placeholder="Подключить"
                maxLength={50}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="button_share_text" className="settings-label">
                Текст кнопки "Поделиться"
              </label>
              <input
                type="text"
                id="button_share_text"
                value={settings.button_share_text}
                onChange={(e) => handleChange('button_share_text', e.target.value)}
                className="settings-input"
                placeholder="Поделиться"
                maxLength={50}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="button_copy_text" className="settings-label">
                Текст кнопки "Копировать"
              </label>
              <input
                type="text"
                id="button_copy_text"
                value={settings.button_copy_text}
                onChange={(e) => handleChange('button_copy_text', e.target.value)}
                className="settings-input"
                placeholder="Копировать"
                maxLength={50}
              />
            </div>
          </div>

          {/* Дополнительные тексты */}
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>📝 Дополнительные тексты</h3>
            
            <div className="settings-form-group">
              <label htmlFor="subscription_active_text" className="settings-label">
                Текст для активной подписки
              </label>
              <input
                type="text"
                id="subscription_active_text"
                value={settings.subscription_active_text}
                onChange={(e) => handleChange('subscription_active_text', e.target.value)}
                className="settings-input"
                placeholder="Подписка активна"
                maxLength={200}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="subscription_expired_text" className="settings-label">
                Текст для истекшей подписки
              </label>
              <input
                type="text"
                id="subscription_expired_text"
                value={settings.subscription_expired_text}
                onChange={(e) => handleChange('subscription_expired_text', e.target.value)}
                className="settings-input"
                placeholder="Подписка истекла"
                maxLength={200}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="subscription_trial_text" className="settings-label">
                Текст для пробного периода
              </label>
              <input
                type="text"
                id="subscription_trial_text"
                value={settings.subscription_trial_text}
                onChange={(e) => handleChange('subscription_trial_text', e.target.value)}
                className="settings-input"
                placeholder="Пробный период"
                maxLength={200}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="balance_label_text" className="settings-label">
                Текст метки "Баланс"
              </label>
              <input
                type="text"
                id="balance_label_text"
                value={settings.balance_label_text}
                onChange={(e) => handleChange('balance_label_text', e.target.value)}
                className="settings-input"
                placeholder="Баланс"
                maxLength={50}
              />
            </div>

            <div className="settings-form-group">
              <label htmlFor="referral_code_label_text" className="settings-label">
                Текст метки "Реферальный код"
              </label>
              <input
                type="text"
                id="referral_code_label_text"
                value={settings.referral_code_label_text}
                onChange={(e) => handleChange('referral_code_label_text', e.target.value)}
                className="settings-input"
                placeholder="Реферальный код"
                maxLength={50}
              />
            </div>
          </div>

          {/* Мета-теги */}
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>🔍 Мета-теги (SEO)</h3>
            
            <div className="settings-form-group">
              <label htmlFor="favicon_url" className="settings-label">
                URL Favicon
              </label>
              <input
                type="url"
                id="favicon_url"
                value={settings.favicon_url}
                onChange={(e) => handleChange('favicon_url', e.target.value)}
                className="settings-input"
                placeholder="https://example.com/favicon.ico"
              />
              <p className="settings-hint">
                URL иконки сайта (favicon). Рекомендуемый размер: 32x32px или 16x16px.
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="meta_title" className="settings-label">
                Meta Title
              </label>
              <input
                type="text"
                id="meta_title"
                value={settings.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                className="settings-input"
                placeholder="StealthNET - Безопасный VPN"
                maxLength={200}
              />
              <p className="settings-hint">
                Заголовок страницы для поисковых систем (title тег).
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="meta_description" className="settings-label">
                Meta Description
              </label>
              <textarea
                id="meta_description"
                value={settings.meta_description}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                className="settings-textarea"
                placeholder="Безопасный VPN сервис с высокой скоростью и защитой приватности"
                maxLength={500}
                rows={3}
              />
              <p className="settings-hint">
                Описание страницы для поисковых систем (meta description).
              </p>
            </div>

            <div className="settings-form-group">
              <label htmlFor="meta_keywords" className="settings-label">
                Meta Keywords
              </label>
              <input
                type="text"
                id="meta_keywords"
                value={settings.meta_keywords}
                onChange={(e) => handleChange('meta_keywords', e.target.value)}
                className="settings-input"
                placeholder="VPN, безопасность, приватность, прокси"
                maxLength={300}
              />
              <p className="settings-hint">
                Ключевые слова для поисковых систем (meta keywords). Разделяйте запятыми.
              </p>
            </div>
          </div>

          {/* Секция быстрого скачивания */}
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>📥 Быстрое скачивание</h3>
            <p className="admin-description-text" style={{ marginBottom: '1.5rem' }}>
              Настройте блок "Быстрое скачивание" на странице подписки. Укажите ссылки на ваши клиенты для разных платформ.
            </p>
            
            <div className="settings-form-group">
              <label className="settings-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.quick_download_enabled}
                  onChange={(e) => handleChange('quick_download_enabled', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Показывать блок "Быстрое скачивание"</span>
              </label>
              <p className="settings-hint">
                Если отключить, блок с кнопками скачивания не будет отображаться на странице подписки.
              </p>
            </div>

            {settings.quick_download_enabled && (
              <>
                <div className="settings-form-group">
                  <label htmlFor="quick_download_windows_url" className="settings-label">
                    🪟 Ссылка для Windows
                  </label>
                  <input
                    type="url"
                    id="quick_download_windows_url"
                    value={settings.quick_download_windows_url}
                    onChange={(e) => handleChange('quick_download_windows_url', e.target.value)}
                    className="settings-input"
                    placeholder="https://example.com/downloads/Client-Setup.exe"
                  />
                  <p className="settings-hint">
                    Прямая ссылка на установщик Windows. Оставьте пустым, чтобы скрыть кнопку.
                  </p>
                </div>

                <div className="settings-form-group">
                  <label htmlFor="quick_download_android_url" className="settings-label">
                    🤖 Ссылка для Android
                  </label>
                  <input
                    type="url"
                    id="quick_download_android_url"
                    value={settings.quick_download_android_url}
                    onChange={(e) => handleChange('quick_download_android_url', e.target.value)}
                    className="settings-input"
                    placeholder="https://example.com/downloads/client.apk"
                  />
                  <p className="settings-hint">
                    Прямая ссылка на APK файл. Оставьте пустым, чтобы скрыть кнопку.
                  </p>
                </div>

                <div className="settings-form-group">
                  <label htmlFor="quick_download_macos_url" className="settings-label">
                    🍎 Ссылка для macOS
                  </label>
                  <input
                    type="url"
                    id="quick_download_macos_url"
                    value={settings.quick_download_macos_url}
                    onChange={(e) => handleChange('quick_download_macos_url', e.target.value)}
                    className="settings-input"
                    placeholder="https://example.com/downloads/Client.dmg"
                  />
                  <p className="settings-hint">
                    Прямая ссылка на установщик macOS. Оставьте пустым, чтобы скрыть кнопку.
                  </p>
                </div>

                <div className="settings-form-group">
                  <label htmlFor="quick_download_ios_url" className="settings-label">
                    📱 Ссылка для iOS
                  </label>
                  <input
                    type="url"
                    id="quick_download_ios_url"
                    value={settings.quick_download_ios_url}
                    onChange={(e) => handleChange('quick_download_ios_url', e.target.value)}
                    className="settings-input"
                    placeholder="https://apps.apple.com/app/..."
                  />
                  <p className="settings-hint">
                    Ссылка на App Store или TestFlight. Оставьте пустым, чтобы скрыть кнопку.
                  </p>
                </div>

                <div className="settings-form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px dashed var(--border-color)' }}>
                  <label htmlFor="quick_download_profile_deeplink" className="settings-label">
                    🔗 Deeplink для кнопки "Добавить профиль"
                  </label>
                  <input
                    type="text"
                    id="quick_download_profile_deeplink"
                    value={settings.quick_download_profile_deeplink}
                    onChange={(e) => handleChange('quick_download_profile_deeplink', e.target.value)}
                    className="settings-input"
                    placeholder="stealthnet://install-config?url="
                  />
                  <p className="settings-hint">
                    Deeplink схема вашего приложения. К ней будет добавлена ссылка подписки пользователя.<br/>
                    <strong>Пример:</strong> <code>stealthnet://install-config?url=</code> → <code>stealthnet://install-config?url=https://...</code>
                  </p>
                </div>
              </>
            )}
          </div>
          
          <div className="settings-actions">
            <button 
              type="submit" 
              className="btn" 
              style={{ maxWidth: '200px' }}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
            {saveStatus === 'success' && (
              <div className="message-success">Сохранено!</div>
            )}
          </div>
        </form>
      </section>
    </main>
  );
}

export default AdminBrandingPage;

